import {useEffect, useState} from 'react';
import {AdminColumnsEnum, TeamColumnsEnum} from '../const/dataEnum';
import {FmlxButton, FmlxIcon} from 'fmlx-common-ui';

/**
 * Custom hook to render table data based on the provided path name and selected tab.
 *
 * @param {string} pathName - The current path name.
 * @param {number} selectedTab - The currently selected tab index.
 * ```
 * selectedTab : 0 = All messsage, 1 = Sent Message
 * ```
 * @param {function} handleOnOpenModal - Function to handle opening the modal.
 * @returns {Object} An object containing the table columns.
 * returns Example :
 * ```
 * [{id: "sender", Header: "Sender", accessor: "sender", filter:'text | number | date'}]
 * ```
 * @param {ObjectConstructor} ActionButton - setting up an custom action button if neccesarry to handle modal interactions
 * @returns {Object} An object containing the table columns with custom action button.
 * return example :
 * ```
 * [{id: "actions", Header: "Action", accessor: "employeSentTab", Cell: ({ row }) => (<div>...</div>)}]
 * ```
 */

export const useRenderTableData = (pathName, selectedTab, handleOnOpenModal, className) => {
  const [dataPerColumns, setTableColumns] = useState([]);

  // message data action -> custom action
  const ActionButton = Object.freeze({
    ACTIONS: {
      id: 'actions',
      Header: 'Action',
      accessor: 'employeSentTab',
      Cell: ({row}) => (
        <div className={className}>
          <FmlxButton
            className="fmlx-button"
            label="VIEW MESSAGE"
            type="primary"
            variant="solid"
            icon={<FmlxIcon name="Eye"/>}
            withIcon="start"
            onClick={() => handleOnOpenModal(row)}
          />
        </div>
      ),
    },
    ICON_ACTION: {
      id: 'actions',
      className: 'actionCell',
      accessor: 'employeSentTab',
      Cell: ({row}) => (
        <div style={{cursor: 'pointer'}} onClick={() => handleOnOpenModal(row)}>
          <FmlxIcon name={'Eye'}/>
        </div>
      ),
    },
  });

  useEffect(() => {
    setTableColumns(generateTableColumns(pathName, selectedTab));
  }, [pathName, selectedTab]);

  const generateTableColumns = (pathName, selectedTab) => {
    const isEmployee = pathName.includes('/Team');
    if (isEmployee) {
      switch (selectedTab) {
        case 0:
          return [
            TeamColumnsEnum.SENDER,
            TeamColumnsEnum.DATE,
            TeamColumnsEnum.TYPE,
            TeamColumnsEnum.COMMENT,
            ActionButton.ICON_ACTION,
          ];
        case 1:
          return [
            TeamColumnsEnum.RECEIVER,
            TeamColumnsEnum.DATE,
            TeamColumnsEnum.TYPE,
            TeamColumnsEnum.COMMENT,
            ActionButton.ICON_ACTION,
          ];
        default:
          return [
            AdminColumnsEnum.SENDER,
            AdminColumnsEnum.RECEIVER,
            AdminColumnsEnum.TYPE,
            AdminColumnsEnum.DATE,
            AdminColumnsEnum.STATUS,
            ActionButton.ACTIONS,
          ];
      }
    } else {
      return [
        AdminColumnsEnum.SENDER,
        AdminColumnsEnum.RECEIVER,
        AdminColumnsEnum.TYPE,
        AdminColumnsEnum.DATE,
        AdminColumnsEnum.STATUS,
        ActionButton.ACTIONS,
      ];
    }
  };

  return {
    dataPerColumns,
  };
};
